import { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import * as Icons from 'lucide-react'
import {
  Trophy, Crown, Medal, Target, Flame, Coins, Calendar, ArrowLeft, Users, Zap,
  Edit3, Save, Gamepad2, Settings, Upload,
} from 'lucide-react'
import { DiscordIcon, TwitchIcon, SteamIcon } from '@/components/BrandIcons'
import { rarityColor } from '@/lib/rarity'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { playersAPI, badgeAPI, composAPI, profileItemsAPI, userAPI } from '@/lib/api'
import KassaAccountCard from '@/components/kassa/KassaAccountCard'
import RaffleAdminWidget from '@/components/raffle/RaffleAdminWidget'
import ImageCropper from '@/components/ImageCropper'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/hooks/useAuth'
import {
  ProfileHero, StatStrip, TrophyHall, AchievementPanel, MatchLog, SectionHeader, Avatar,
} from '@/components/profile/ProfileFrame'

/* Speler-profielpagina in HUD-stijl. De signature is de corner-brackets
   rond de hero (zie ProfileFrame). Al de rest — trophy hall, achievement
   panel, match log — is bewust gedempt zodat de brackets en de grote naam
   het beeld dragen.

   Level-berekening is een simpele formule uit compo-participatie + punten,
   ontworpen om al vroeg te tikken (niveau 1-3 vanaf de eerste compo). */

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('nl-BE', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'

function berekenLevel({ points = 0, podiums = 0, played = 0 }) {
  const xp = points + podiums * 50 + played * 10
  const lvl = Math.max(1, Math.floor(Math.sqrt(xp / 25)))
  const nextAt = 25 * (lvl + 1) * (lvl + 1)
  const prevAt = 25 * lvl * lvl
  const inLvl = Math.max(0, Math.min(1, (xp - prevAt) / (nextAt - prevAt)))
  return { level: lvl, xp, nextAt, inLvl }
}

export default function PlayerProfilePage() {
  const { userId } = useParams()
  const [searchParams] = useSearchParams()
  const { user: currentUser, isAdmin } = useAuth()
  const [data, setData]       = useState(null)
  const [compoResults, setCompo] = useState([])
  const [badgeTypes, setBadgeTypes] = useState([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [loadoutOpen, setLoadoutOpen] = useState(false)
  const [inventaris, setInventaris] = useState(null)  // { items, equipped }
  const [bioDlg, setBioDlg] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [cropperOpen, setCropperOpen] = useState(false)
  const [avatarSrc, setAvatarSrc] = useState(null)
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [bannerBusy, setBannerBusy] = useState(false)
  const [bannerCropOpen, setBannerCropOpen] = useState(false)
  const [bannerSrc, setBannerSrc] = useState(null)
  const fileRef = useRef(null)
  const bannerRef = useRef(null)

  const kiesAvatar = (e) => {
    const f = e.target.files?.[0]
    if (!f) return
    if (f.size > 5 * 1024 * 1024) { window.alert('Bestand mag max 5MB zijn'); return }
    setAvatarSrc(URL.createObjectURL(f))
    setCropperOpen(true)
    e.target.value = ''
  }
  const uploadAvatar = async (blob) => {
    setAvatarBusy(true)
    try {
      const fd = new FormData()
      fd.append('avatar', new File([blob], 'avatar.jpg', { type: 'image/jpeg' }))
      await userAPI.uploadAvatar(fd)
      laad()
    } catch (e) { window.alert(e.response?.data?.error || 'Upload mislukt') }
    finally { setAvatarBusy(false); setCropperOpen(false) }
  }

  const kiesBanner = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (f.size > 5 * 1024 * 1024) { window.alert('Bestand mag max 5MB zijn'); return }
    setBannerSrc(URL.createObjectURL(f))
    setBannerCropOpen(true)
  }
  const uploadBanner = async (blob) => {
    setBannerBusy(true)
    try {
      const fd = new FormData()
      fd.append('banner', new File([blob], 'banner.jpg', { type: 'image/jpeg' }))
      await userAPI.uploadBanner(fd)
      laad()
    } catch (err) { window.alert(err.response?.data?.error || 'Banner uploaden mislukt') }
    finally { setBannerBusy(false); setBannerCropOpen(false) }
  }
  const zetBannerBron = async (source) => {
    try { await userAPI.setBannerSource(source); laad() }
    catch (err) { window.alert(err.response?.data?.error || err.message) }
  }
  const verwijderCustomBanner = async () => {
    if (!window.confirm('Eigen banner verwijderen?')) return
    try { await userAPI.deleteBanner(); laad() }
    catch (err) { window.alert(err.response?.data?.error || err.message) }
  }

  const laad = useCallback(() => {
    setLoading(true)
    playersAPI.get(userId)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.error ?? e.message))
      .finally(() => setLoading(false))
    // Compo-resultaten voor deze user — voor Trophy Hall + Match Log.
    composAPI.listResults({ limit: 200 })
      .then(r => setCompo((r.data?.results ?? []).filter(x => x.user_id === Number(userId))))
      .catch(() => {})
  }, [userId])

  useEffect(() => { laad() }, [laad])

  useEffect(() => {
    if (!isAdmin) return
    badgeAPI.getAllTypes().then(r => setBadgeTypes(r.data?.badges ?? [])).catch(() => {})
  }, [isAdmin])

  // ?open=pimp → auto-open LoadoutPaneel + fetch inventaris. Getriggerd door
  // notif-links "banner ontgrendeld" zodat de user niet zelf hoeft te zoeken.
  useEffect(() => {
    if (searchParams.get('open') !== 'pimp') return
    if (!currentUser || currentUser.id !== Number(userId)) return
    profileItemsAPI.mine()
      .then(r => { setInventaris(r.data); setLoadoutOpen(true) })
      .catch(() => {})
  }, [searchParams, currentUser, userId])

  const kenBadgeToe = async (bId) => {
    try { await badgeAPI.awardBadge(Number(userId), bId); setPickerOpen(false); laad() }
    catch (e) { window.alert(e.response?.data?.error ?? e.message) }
  }
  const trekBadgeIn = async (bId) => {
    if (!window.confirm('Deze badge terugtrekken?')) return
    try { await badgeAPI.revokeBadge(Number(userId), bId); laad() }
    catch (e) { window.alert(e.response?.data?.error ?? e.message) }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Laden…</p>
  if (error)   return <p className="text-sm text-signal">{error}</p>
  if (!data?.player) return <p className="text-sm text-muted-foreground">Speler niet gevonden.</p>

  const { player, stats, badges, points, loadout } = data
  const name = player.display_name || player.username
  const podiums = (stats.tournaments_won || 0) + (stats.tournaments_second || 0) + (stats.tournaments_third || 0)
  const goud = compoResults.filter(r => r.place === 1).length
  const zilver = compoResults.filter(r => r.place === 2).length
  const brons = compoResults.filter(r => r.place === 3).length

  // Accent: geëquipeerde accent wint; anders goud voor winnaars, acid als default.
  const accent = loadout?.accent?.config?.color || (goud > 0 ? '#E4C265' : '#B4FF39')
  const isEigenProfiel = currentUser?.id === Number(userId)
  const lvl = berekenLevel({ points: points.balance, podiums, played: stats.tournaments_played })

  return (
    <div>
      <Button asChild variant="ghost" size="sm" className="mb-3">
        <Link to="/spelers"><ArrowLeft className="h-4 w-4 mr-1" />Alle spelers</Link>
      </Button>

      <ProfileHero
        accent={accent}
        banner={loadout?.banner}
        customBannerUrl={player.custom_banner_url}
        bannerSource={player.banner_source}
        roleBadge={(badges || []).find(b => ['Admin','Helpdesk','Referee'].includes(b.name))}
        eyebrow="Speler-profiel"
        name={name}
        handle={`@${player.username}${player.role !== 'user' ? ` · ${player.role}` : ''}`}
        meta={
          <div className="flex items-center gap-1.5 pf-mono text-[11px] uppercase tracking-widest">
            <Calendar className="h-3.5 w-3.5" />
            Speelt mee sinds {fmtDate(player.created_at)}
          </div>
        }
        avatar={<Avatar url={player.avatar_url} name={name} accent={accent} frame={loadout?.frame} />}
        right={
          <div className="flex flex-col items-end gap-3">
            <div className="pf-mono text-[10px] uppercase tracking-[0.35em] text-muted-foreground">
              Level {lvl.level}
            </div>
            <div className="relative w-40 h-1.5 bg-border rounded-full overflow-hidden">
              <div className="absolute inset-y-0 left-0 rounded-full"
                   style={{ width: `${lvl.inLvl * 100}%`, background: accent }} />
            </div>
            <div className="pf-mono text-[10px] tabular-nums text-muted-foreground">
              {lvl.xp} / {lvl.nextAt} XP
            </div>
            {isEigenProfiel && (
              <div className="flex flex-wrap justify-end gap-1.5">
                <input ref={fileRef}   type="file" accept="image/*" className="hidden" onChange={kiesAvatar} />
                <input ref={bannerRef} type="file" accept="image/*" className="hidden" onChange={kiesBanner} />
                <Button size="sm" variant="outline"
                        onClick={() => fileRef.current?.click()}
                        disabled={avatarBusy}>
                  <Upload className="h-3 w-3 mr-1" /> Avatar
                </Button>
                <Button size="sm" variant="outline"
                        onClick={() => bannerRef.current?.click()}
                        disabled={bannerBusy}>
                  <Upload className="h-3 w-3 mr-1" /> {player.custom_banner_url ? 'Banner wijzigen' : 'Banner'}
                </Button>
                {player.custom_banner_url && (
                  <Button size="sm" variant="outline"
                          onClick={verwijderCustomBanner}
                          disabled={bannerBusy}
                          title="Eigen banner verwijderen">
                    Banner verwijderen
                  </Button>
                )}
                {/* Toon altijd zodra er meer dan één bron bestaat, ook als de
                    huidige bron niet-actief is (bv. user heeft custom banner
                    én kreeg net een inventaris-banner via grant). Zonder deze
                    picker zou de nieuwe grant onzichtbaar blijven. */}
                {(!!player.custom_banner_url || !!loadout?.banner) && (
                  <BannerBronKiezer
                    huidig={player.banner_source || (player.custom_banner_url ? 'custom' : 'inventory')}
                    onKies={zetBannerBron}
                  />
                )}
                <Button size="sm" variant="outline"
                        onClick={async () => {
                          // Altijd refetchen — user kan tussentijds een nieuwe
                          // grant hebben gekregen (via notif) waardoor de
                          // cache stale is.
                          try { const r = await profileItemsAPI.mine(); setInventaris(r.data) } catch {}
                          setLoadoutOpen(o => !o)
                        }}>
                  Pimp
                </Button>
                <Button asChild size="sm" variant="outline">
                  <Link to="/instellingen"><Settings className="h-3 w-3 mr-1" /> Instellingen</Link>
                </Button>
              </div>
            )}
          </div>
        }
      />

      {(player.bio || player.favorite_games?.length || Object.keys(player.socials || {}).length > 0 || isEigenProfiel) && (
        <BioBlok player={player} isEigen={isEigenProfiel} accent={accent}
                 onEdit={() => setBioDlg({
                   bio: player.bio || '',
                   favorite_games: player.favorite_games || [],
                   socials: player.socials || {},
                   profile_visibility: player.profile_visibility || 'lan',
                 })} />
      )}

      {bioDlg && (
        <BioDialog dlg={bioDlg} setDlg={setBioDlg}
                   onSave={async () => {
                     try {
                       await userAPI.updateProfile({
                         bio: bioDlg.bio,
                         favorite_games: bioDlg.favorite_games,
                         socials: bioDlg.socials,
                         profile_visibility: bioDlg.profile_visibility,
                       })
                       setBioDlg(null); laad()
                     } catch (e) { window.alert(e.response?.data?.error ?? e.message) }
                   }} />
      )}

      {isEigenProfiel && loadoutOpen && inventaris && (
        <LoadoutPaneel
          items={inventaris.items}
          equipped={inventaris.equipped}
          onEquip={async (slot, itemId) => {
            try {
              await profileItemsAPI.equip(slot, itemId)
              const r = await profileItemsAPI.mine(); setInventaris(r.data)
              laad()
            } catch (e) { window.alert(e.response?.data?.error ?? e.message) }
          }}
          onSluit={() => setLoadoutOpen(false)}
        />
      )}

      <div className="mt-4">
        <StatStrip accent={accent} items={[
          { label: 'Punten',      value: points.balance,          icon: Coins,  color: '#E4C265' },
          { label: 'Compo goud',  value: goud || stats.tournaments_won || 0, icon: Crown, color: '#E4C265' },
          { label: 'Podia',       value: podiums,                 icon: Medal,  color: '#B4FF39' },
          { label: 'Beste reeks', value: stats.best_streak || 0,  icon: Flame,  color: '#FF5A5A' },
        ]} />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          {(() => {
            // Trophy Hall combineert compo-podiums (KV/IC/handmatige toernooi-
            // uitslagen uit compo_results) met de automatische toernooi-podiums
            // uit /api/players/:id. Dedup op (kind, source_id) zodat een
            // toernooi waar toevallig ook een handmatige compo-entry voor
            // bestaat, niet dubbel verschijnt.
            const seenTourIds = new Set(
              compoResults.filter(r => r.kind === 'tournament' && r.source_id)
                          .map(r => Number(r.source_id))
            )
            const tourPodia = (data.tournaments || [])
              .filter(t => t.placement && t.placement <= 3 && !seenTourIds.has(t.id))
              .map(t => ({
                place: t.placement,
                kind: 'tournament',
                source_name: t.name,
                ended_at: null,
                id: `t-${t.id}`,
                source_id: t.id,
                meta: t.placement_label ? { label: t.placement_label } : undefined,
                edition_name: t.edition_name,
              }))
            const alle = [...compoResults, ...tourPodia]
            // Prefs (verbergen/rankschikken) toepassen. Owner ziet ook verborgen
            // (met opacity via _hidden); anderen krijgen ze niet meegestuurd.
            const prefs = data.trophy_prefs || {}
            const isOwner = data.viewer?.is_owner
            const verrijkt = alle.map(r => {
              const key = `${r.kind}:${r.source_id}`
              const p = prefs[key]
              return { ...r, _hidden: !!p?.hidden, _order: p?.sort_order }
            })
            const gefilterd = isOwner ? verrijkt : verrijkt.filter(r => !r._hidden)
            gefilterd.sort((a, b) => {
              const oa = a._order == null ? Infinity : a._order
              const ob = b._order == null ? Infinity : b._order
              if (oa !== ob) return oa - ob
              return 0    // stabiel — behoudt originele volgorde bij gelijke sort_order
            })
            return gefilterd.length > 0
              ? <TrophyHall
                  results={gefilterd}
                  isOwner={isOwner}
                  onToggleHidden={async (key, hidden) => {
                    try { await playersAPI.setTrophyVisibility(key, hidden); laad() }
                    catch (e) { window.alert(e.response?.data?.error ?? e.message) }
                  }}
                  onMove={async (key, delta) => {
                    const idx = gefilterd.findIndex(r => `${r.kind}:${r.source_id}` === key)
                    if (idx < 0) return
                    const nieuweIdx = idx + delta
                    if (nieuweIdx < 0 || nieuweIdx >= gefilterd.length) return
                    const volgorde = gefilterd.map(r => `${r.kind}:${r.source_id}`)
                    volgorde.splice(idx, 1)
                    volgorde.splice(nieuweIdx, 0, key)
                    try { await playersAPI.reorderTrophies(volgorde); laad() }
                    catch (e) { window.alert(e.response?.data?.error ?? e.message) }
                  }}
                />
              : null
          })()}
          <AchievementPanel
            badges={badges}
            canManage={isAdmin}
            isOwner={currentUser?.id === Number(userId)}
            onAdd={() => setPickerOpen(o => !o)}
            onRemove={trekBadgeIn}
            onToggleHidden={async (badgeTypeId, hidden) => {
              try { await badgeAPI.setMyBadgeVisibility(badgeTypeId, hidden); laad() }
              catch (e) { window.alert(e.response?.data?.error ?? e.message) }
            }}
            onMove={async (badgeTypeId, delta) => {
              const idx = badges.findIndex(b => b.id === badgeTypeId)
              if (idx < 0) return
              const nieuweIdx = idx + delta
              if (nieuweIdx < 0 || nieuweIdx >= badges.length) return
              const volgorde = badges.map(b => b.id)
              volgorde.splice(idx, 1)
              volgorde.splice(nieuweIdx, 0, badgeTypeId)
              try { await badgeAPI.reorderMyBadges(volgorde); laad() }
              catch (e) { window.alert(e.response?.data?.error ?? e.message) }
            }}
          />
          {isAdmin && pickerOpen && (
            <div className="rounded-[6px] border border-dashed border-border p-3">
              <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                Kies een badge om toe te kennen
              </div>
              <div className="flex flex-wrap gap-1.5">
                {badgeTypes.filter(bt => !badges.some(b => b.id === bt.id)).map(bt => (
                  <button key={bt.id} type="button" onClick={() => kenBadgeToe(bt.id)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] border text-xs hover:border-acid transition-colors"
                          style={{ borderLeft: `2px solid ${bt.color || '#B4FF39'}` }}>
                    <Trophy className="h-3 w-3" style={{ color: bt.color || '#B4FF39' }} />
                    {bt.name}
                  </button>
                ))}
                {badgeTypes.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Nog geen badges. Ga naar <Link to="/admin/badges" className="text-acid underline">/admin/badges</Link>.
                  </p>
                )}
                {badgeTypes.length > 0 && badgeTypes.every(bt => badges.some(b => b.id === bt.id)) && (
                  <p className="text-xs text-muted-foreground">Deze speler heeft al alle bestaande badges.</p>
                )}
              </div>
            </div>
          )}
          {(() => {
            // Match log combineert compo-uitslagen met alle toernooien (podium
            // of niet) waar de speler aan meedeed, ontdubbeld op source_id.
            const seenTourIds = new Set(
              compoResults.filter(r => r.kind === 'tournament' && r.source_id)
                          .map(r => Number(r.source_id))
            )
            const tourRijen = (data.tournaments || [])
              .filter(t => !seenTourIds.has(t.id))
              .map(t => ({
                place: t.placement ?? 99,
                kind: 'tournament',
                source_name: t.name,
                source_id: t.id,
                ended_at: t.updated_at,
                id: `t-${t.id}`,
                points: Number(t.points) || 0,
                meta: t.placement_label ? { label: t.placement_label } : undefined,
                edition_name: t.edition_name,
              }))
            const alle = [...compoResults, ...tourRijen]
            return alle.length > 0 ? <MatchLog results={alle} /> : null
          })()}
        </div>

        {isEigenProfiel && (
          <>
            <ImageCropper open={cropperOpen} imageSrc={avatarSrc}
                          onClose={() => setCropperOpen(false)}
                          onCropComplete={uploadAvatar} />
            {/* Banner-cropper: brede aspect ratio 3:1 (1500×500 px)
                zodat de banner ook op smalle schermen goed schaalt. */}
            <ImageCropper open={bannerCropOpen} imageSrc={bannerSrc}
                          onClose={() => setBannerCropOpen(false)}
                          onCropComplete={uploadBanner}
                          aspectRatio={3}
                          shape="rect"
                          outputSize={{ width: 1500, height: 500 }}
                          title="Banner bijsnijden en positioneren" />
          </>
        )}

        <div className="space-y-6">
          {/* Kassa-saldo — alleen op eigen profiel. Andermans rekening is
              privé; toon je zelf mag het wel op je publieke pagina staan. */}
          {isEigenProfiel && <KassaAccountCard />}
          {isAdmin && <RaffleAdminWidget userId={userId} />}

          <div className="pf-hud">
            <SectionHeader label="Record" />
            <div className="rounded-[6px] border border-border bg-card overflow-hidden">
              <RecordRow label="Toernooien gespeeld" value={stats.tournaments_played || 0} />
              <RecordRow label="Matches gespeeld"    value={stats.matches_played || 0} />
              <RecordRow label="Gewonnen"            value={stats.matches_won || 0} accent={accent} />
              <RecordRow label="Verloren"            value={stats.matches_lost || 0} muted />
              <RecordRow label="Win-rate"            value={`${Math.round((stats.win_rate || 0) * (stats.win_rate <= 1 ? 100 : 1))}%`} />
              <RecordRow label="Zilver"              value={zilver} />
              <RecordRow label="Brons"               value={brons} />
            </div>
          </div>

          {points.recent?.length > 0 && (
            <div className="pf-hud">
              <SectionHeader label="Recente punten" count={points.recent.length} />
              <div className="rounded-[6px] border border-border bg-card overflow-hidden">
                {points.recent.slice(0, 8).map(p => (
                  <div key={p.id} className="flex items-center gap-3 px-3 py-2 border-b border-border/40 last:border-0">
                    <span className={`pf-name text-sm tabular-nums w-14 text-right ${p.amount >= 0 ? 'text-acid' : 'text-signal'}`}>
                      {p.amount >= 0 ? '+' : ''}{p.amount}
                    </span>
                    <span className="text-xs flex-1 truncate">{p.reason}</span>
                    <span className="pf-mono text-[10px] text-muted-foreground shrink-0">
                      {new Date(p.created_at).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="pf-hud">
            <SectionHeader label="Snel naar" />
            <div className="grid gap-2">
              <Link to="/teams" className="rounded-[6px] border border-border bg-card px-3 py-2 hover:border-acid transition-colors flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-acid" /> Mijn teams
              </Link>
              <Link to="/compos" className="rounded-[6px] border border-border bg-card px-3 py-2 hover:border-acid transition-colors flex items-center gap-2 text-sm">
                <Trophy className="h-4 w-4 text-gold" /> Compo-overzicht
              </Link>
              <Link to="/tournaments" className="rounded-[6px] border border-border bg-card px-3 py-2 hover:border-acid transition-colors flex items-center gap-2 text-sm">
                <Zap className="h-4 w-4 text-peach" /> Actieve toernooien
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function RecordRow({ label, value, accent, muted }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/40 last:border-0">
      <span className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`pf-name text-lg tabular-nums ${muted ? 'text-muted-foreground' : ''}`}
            style={{ color: accent || undefined }}>
        {value}
      </span>
    </div>
  )
}

/* ─── Loadout paneel: eigen inventaris + slot-select ───────── */

const SLOT_LABEL = {
  banner:       'Banner',
  avatar_frame: 'Avatar-kader',
  accent:       'Accent-kleur',
}
// Rarity-tokens komen uit @/lib/rarity — proxy zodat lookups direct
// via de helper gaan (drie profielpagina's houden geen eigen kopie meer).
const RARITY_KLEUR = new Proxy({}, { get: (_, r) => rarityColor(r) })

/* ─── Bio-blok + edit-dialog ───────────────────────────────── */

function BioBlok({ player, isEigen, accent, onEdit }) {
  const heeftContent = !!(player.bio || player.favorite_games?.length || Object.keys(player.socials || {}).length)
  return (
    <div className="rounded-[6px] border border-border bg-card p-4 my-4">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[16rem]">
          {player.bio ? (
            <p className="text-sm leading-relaxed">{player.bio}</p>
          ) : isEigen ? (
            <p className="text-sm text-muted-foreground italic">
              Nog geen bio. Vertel iets over jezelf — je favoriete games, je stijl, je hate/love-list.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground italic">Deze speler heeft nog geen bio geschreven.</p>
          )}
          {player.favorite_games?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {player.favorite_games.map((g, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-[4px] border border-border px-2 py-0.5 text-xs"
                      style={{ color: accent, borderColor: `${accent}44` }}>
                  <Gamepad2 className="h-3 w-3" /> {g}
                </span>
              ))}
            </div>
          )}
          {Object.keys(player.socials || {}).length > 0 && (
            <div className="flex flex-wrap gap-3 mt-3">
              {player.socials.discord && (
                <button type="button"
                        onClick={() => { try { navigator.clipboard?.writeText(player.socials.discord) } catch {} }}
                        title="Kopieer discord-tag"
                        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-acid transition-colors">
                  <span style={{ color: '#5865F2' }}><DiscordIcon className="h-3.5 w-3.5" /></span>
                  {player.socials.discord}
                </button>
              )}
              {player.socials.twitch && (
                <a href={`https://twitch.tv/${player.socials.twitch}`} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-acid transition-colors">
                  <span style={{ color: '#9146FF' }}><TwitchIcon className="h-3.5 w-3.5" /></span>
                  twitch.tv/{player.socials.twitch}
                </a>
              )}
              {player.socials.steam && (
                <a href={`https://steamcommunity.com/id/${player.socials.steam}`} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-acid transition-colors">
                  <SteamIcon className="h-3.5 w-3.5" />
                  steam/{player.socials.steam}
                </a>
              )}
            </div>
          )}
        </div>
        {isEigen && (
          <Button size="sm" variant="outline" onClick={onEdit}>
            <Edit3 className="h-4 w-4 mr-1" />
            {heeftContent ? 'Bewerken' : 'Vertel iets'}
          </Button>
        )}
      </div>
    </div>
  )
}

function BioDialog({ dlg, setDlg, onSave }) {
  const games = dlg.favorite_games || []
  const socials = dlg.socials || {}
  return (
    <Dialog open onOpenChange={(o) => !o && setDlg(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Bewerk je profiel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Bio (max 500 tekens)</Label>
            <textarea value={dlg.bio} onChange={e => setDlg({ ...dlg, bio: e.target.value })}
                      rows={4} maxLength={500}
                      className="w-full rounded-[4px] border border-border bg-background px-3 py-2 text-sm"
                      placeholder="Wie ben je op deze LAN? Wat speel je graag? Wat is je stijl?" />
            <p className="text-xs text-muted-foreground text-right mt-1">{(dlg.bio || '').length}/500</p>
          </div>
          <div>
            <Label>Favoriete games (max 8)</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {games.map((g, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-[4px] border border-border px-2 py-1 text-xs">
                  {g}
                  <button type="button" onClick={() => setDlg({ ...dlg, favorite_games: games.filter((_, j) => j !== i) })}>
                    <Icons.X className="h-3 w-3 text-signal" />
                  </button>
                </span>
              ))}
            </div>
            <Input placeholder="Type een game + Enter"
                   onKeyDown={e => {
                     if (e.key === 'Enter' && e.target.value.trim() && games.length < 8) {
                       setDlg({ ...dlg, favorite_games: [...games, e.target.value.trim()] })
                       e.target.value = ''; e.preventDefault()
                     }
                   }} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Discord</Label>
              <Input value={socials.discord || ''}
                     onChange={e => setDlg({ ...dlg, socials: { ...socials, discord: e.target.value } })}
                     placeholder="naam#1234" />
            </div>
            <div>
              <Label>Twitch</Label>
              <Input value={socials.twitch || ''}
                     onChange={e => setDlg({ ...dlg, socials: { ...socials, twitch: e.target.value } })}
                     placeholder="username" />
            </div>
            <div>
              <Label>Steam</Label>
              <Input value={socials.steam || ''}
                     onChange={e => setDlg({ ...dlg, socials: { ...socials, steam: e.target.value } })}
                     placeholder="vanity-id" />
            </div>
          </div>
          <div>
            <Label>Zichtbaarheid</Label>
            <select value={dlg.profile_visibility}
                    onChange={e => setDlg({ ...dlg, profile_visibility: e.target.value })}
                    className="w-full h-9 rounded-[4px] border border-border bg-background px-2 text-sm">
              <option value="lan">LAN — alleen ingelogde deelnemers</option>
              <option value="public">Publiek — iedereen die het profiel bezoekt</option>
              <option value="private">Privé — alleen jij en admins</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setDlg(null)}>Annuleer</Button>
          <Button type="button" onClick={onSave}><Save className="h-4 w-4 mr-1" /> Opslaan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LoadoutPaneel({ items, equipped, onEquip, onSluit }) {
  const perSlot = { banner: [], avatar_frame: [], accent: [] }
  for (const it of items) if (perSlot[it.kind]) perSlot[it.kind].push(it)
  const eqId = { banner: equipped.equipped_banner_id, avatar_frame: equipped.equipped_frame_id, accent: equipped.equipped_accent_id }

  return (
    <div className="mt-4 rounded-[6px] border border-border bg-card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="pf-name text-lg">Pimp je profiel</div>
          <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Kies per slot uit je inventaris
          </div>
        </div>
        <button onClick={onSluit} className="text-sm text-muted-foreground hover:text-foreground">
          Sluiten
        </button>
      </div>

      {['banner', 'avatar_frame', 'accent'].map(slot => (
        <div key={slot}>
          <div className="pf-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            {SLOT_LABEL[slot]}
          </div>
          {perSlot[slot].length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nog niks in dit slot. Verdien iets via een compo of vraag de admin.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => onEquip(slot, null)}
                      className={`px-3 py-2 rounded-[4px] border text-sm ${eqId[slot] == null ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}>
                — Geen —
              </button>
              {perSlot[slot].map(it => {
                const gekozen = eqId[slot] === it.id
                const rk = RARITY_KLEUR[it.rarity] || RARITY_KLEUR.common
                return (
                  <button key={it.id} onClick={() => onEquip(slot, it.id)}
                          className={`px-3 py-2 rounded-[4px] border text-sm text-left ${gekozen ? 'border-acid bg-acid/10' : 'border-border hover:border-acid/40'}`}
                          style={{ borderLeft: `3px solid ${rk}` }}>
                    <div className="font-semibold">{it.name}</div>
                    {it.source_hint && (
                      <div className="pf-mono text-[10px] uppercase tracking-widest" style={{ color: rk }}>
                        {it.rarity} · {it.source_hint}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/* ─── Kleine pill-picker voor de banner-bron ───────────────
   Toont "Eigen" / "Uit inventaris" als segmented control, met een
   klein X-knopje om een custom banner weg te gooien. */
function BannerBronKiezer({ huidig, onKies }) {
  const opties = [
    { id: 'custom',    label: 'Eigen upload' },
    { id: 'inventory', label: 'Uit inventaris' },
  ]
  return (
    <div className="inline-flex items-center rounded-[4px] border border-border bg-card overflow-hidden">
      {opties.map(o => (
        <button key={o.id}
                onClick={() => onKies(o.id)}
                className={`px-2 py-1 text-[11px] uppercase tracking-wider ${
                  huidig === o.id ? 'bg-acid/20 text-acid' : 'text-muted-foreground hover:text-foreground'
                }`}>
          {o.label}
        </button>
      ))}
    </div>
  )
}
